/**
 * Profile, M1 subset of designs/Caged Profile (+ Desktop): editable name, level and XP, record,
 * build label with the group breakdown, unlocked perks, the signature, and the fighter choice
 * (where the paid fighters are bought, D52). League, trophies and rank are hidden until their
 * milestones.
 */
import {
  buildLabel,
  fighterPrice,
  groupPoints,
  MAX_RANK,
  ownsFighter,
  PROGRESSION,
  SKILL_GROUPS,
  SKILLS,
  spentPoints,
  type PerkId,
  type SkillId,
} from '@caged/shared';
import { useEffect, useId, useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useLeague } from '../../state/league';
import { useApp } from '../../state/store';
import { ArenaBackdrop } from '../components/Backdrop';
import { DesktopHeader, TopBar } from '../components/Chrome';
import { bodyUrl, FighterArt } from '../components/FighterArt';
import { LevelCard } from '../components/ProgressWidgets';
import ui from '../components/ui.module.css';
import { useLayoutMode, useManifest, useMyFighter, useMyLook, useNow, useScrollTop } from '../hooks';
import { formatNumber, monthYear } from '../format';
import { currentProgress, levelView, NAME_MAX, perkParams, skillsInGroup } from '../progression';
import '../ui.css';
import s from './ProfileScreen.module.css';

/**
 * Name with an inline editor (Enter saves, Escape cancels). The name is the account's (D55): it
 * closes once the server has it, and stays open with a note when it couldn't be saved.
 */
function NameEditor({ editing, onDone, className }: { editing: boolean; onDone: () => void; className?: string }) {
  const { t } = useTranslation();
  const name = useApp((st) => st.settings.playerName);
  const pickName = useApp((st) => st.pickName);
  const [draft, setDraft] = useState(name);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const inputId = useId();

  if (!editing) return <h1 className={className}>{name}</h1>;

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    const next = draft.trim().slice(0, NAME_MAX);
    if (!next || next === name) return onDone();
    setBusy(true);
    setFailed(false);
    const ok = await pickName(next);
    setBusy(false);
    if (ok) onDone();
    else setFailed(true);
  };

  return (
    <form className={s.nameForm} onSubmit={(e) => void save(e)}>
      <label htmlFor={inputId} className={ui.srOnly}>
        {t('screens.profile.nameLabel')}
      </label>
      <input
        id={inputId}
        className={s.nameInput}
        value={draft}
        maxLength={NAME_MAX}
        autoFocus
        autoComplete="nickname"
        spellCheck={false}
        onFocus={(e) => e.currentTarget.select()}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.preventDefault();
            onDone();
          }
        }}
      />
      <div className={s.nameButtons}>
        <button type="button" className={[ui.ghost, s.smallBtn].join(' ')} onClick={onDone}>
          {t('common.cancel')}
        </button>
        <button type="submit" className={[ui.primary, s.smallBtn].join(' ')} disabled={busy} aria-busy={busy}>
          {t('screens.profile.save')}
        </button>
      </div>
      {failed && (
        <p className={s.saveFailed} role="alert">
          {t('common.saveFailed')}
        </p>
      )}
    </form>
  );
}

function Record() {
  const { t } = useTranslation();
  const r = useApp((st) => st.progress.record);
  const cells = [
    { key: 'wins', val: r.wins, cls: s.green },
    { key: 'losses', val: r.losses, cls: s.dim },
    { key: 'draws', val: r.draws, cls: s.white },
    { key: 'kos', val: r.kos, cls: s.amber },
  ];
  return (
    <div className={s.record}>
      {cells.map((c) => (
        <div key={c.key} className={s.recordCell}>
          <div className={[s.recordVal, c.cls].join(' ')}>{c.val}</div>
          <div className={s.recordLabel}>{t(`screens.profile.record.${c.key}`)}</div>
        </div>
      ))}
    </div>
  );
}

/** The trophy shelf (docs/game-design.md §7.9): the league and national belts won. */
function Belts() {
  const { t } = useTranslation();
  const mine = useLeague((st) => st.mine);
  const load = useLeague((st) => st.load);
  useEffect(() => {
    void load();
  }, [load]);
  if (mine?.status !== 'ok') return null;
  const belts = mine.history.filter((h) => h.belt);
  return (
    <section className={[ui.card, s.belts].join(' ')} aria-labelledby="profile-belts">
      <h2 id="profile-belts" className={ui.label}>
        {t('screens.profile.belts')}
      </h2>
      {belts.length ? (
        <ul className={s.beltList}>
          {belts.map((b) => (
            <li key={b.season} className={[s.belt, b.belt === 'national' ? s.beltGold : ''].join(' ')}>
              <span className={s.beltIcon} aria-hidden="true">
                🏆
              </span>
              <span className={s.beltText}>
                <span className={s.beltName}>{t(b.belt === 'national' ? 'screens.profile.nationalBelt' : 'screens.profile.seriesBelt')}</span>
                <span className={s.beltMeta}>
                  {t('screens.league.season', { season: b.season })} · {t(`screens.league.divisions.${b.name}`)}
                  {b.division > 1 ? ` ${b.series}` : ''}
                </span>
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className={s.beltNone}>{t('screens.profile.noBelts')}</p>
      )}
    </section>
  );
}

function Signature({ fighterId }: { fighterId: string }) {
  const { t } = useTranslation();
  const manifest = useManifest();
  return (
    <div className={s.signature}>
      <div className={s.sigArt} aria-hidden="true">
        <img src={bodyUrl(manifest, fighterId, 'rage')} alt="" />
      </div>
      <div className={s.sigText}>
        <div className={ui.labelDim}>{t('screens.profile.move')}</div>
        <div className={s.sigName}>{t('moves.signature')}</div>
        <div className={s.sigFx}>{t('screens.profile.signatureFx')}</div>
      </div>
    </div>
  );
}

function Perks() {
  const { t } = useTranslation();
  const ranks = useApp((st) => st.progress.ranks);
  const list: { perk: PerkId; skill: SkillId; rank: number }[] = [];
  for (const d of SKILLS) for (const r of [5, 10] as const) if (ranks[d.id] >= r) list.push({ perk: d.perks[r], skill: d.id, rank: r });
  return (
    <div className={s.perks}>
      <div className={s.perksHead}>
        <span>{t('screens.profile.perks')}</span>
        <span>{t('screens.profile.perksCount', { count: list.length, total: SKILLS.length * 2 })}</span>
      </div>
      {list.length ? (
        <div className={s.perkChips}>
          {list.map((p) => (
            <span key={p.perk} className={s.perkChip} title={t(`screens.perks.${p.perk}.fx`, perkParams(p.perk))}>
              <span className={s.gold} aria-hidden="true">
                ★
              </span>
              {t(`screens.perks.${p.perk}.name`)}
              <span className={s.perkWhere}>
                {t(`screens.skills.${p.skill}.name`)} {p.rank}
              </span>
            </span>
          ))}
        </div>
      ) : (
        <div className={s.perksEmpty}>{t('screens.profile.noPerks')}</div>
      )}
    </div>
  );
}

function Skills({ desktop }: { desktop: boolean }) {
  const { t } = useTranslation();
  const go = useApp((st) => st.go);
  const ranks = useApp((st) => st.progress.ranks);
  const fighterId = useMyFighter();
  const gp = groupPoints(ranks);
  const total = spentPoints(ranks);
  const label = buildLabel(ranks);
  const top = SKILL_GROUPS.reduce((a, g) => (gp[g] > gp[a] ? g : a), SKILL_GROUPS[0]!);
  const topOn = label !== 'balanced' && label !== 'none';
  const chip = label === 'none' ? t('screens.builds.none') : t('screens.builds.chip', { label: t(`screens.builds.${label}`) });
  const why =
    label === 'none'
      ? t('screens.profile.whyNone')
      : label === 'balanced'
        ? t('screens.profile.whyBalanced', { pct: Math.round(PROGRESSION.balancedShare * 100) })
        : t('screens.profile.whyTop', { group: t(`screens.groups.${top}`), count: total });
  const toTraining = (
    <button type="button" className={s.toTraining} onClick={() => go('training')}>
      {t('screens.profile.goTraining')}
      <span className={s.dim} aria-hidden="true">
        →
      </span>
    </button>
  );
  return (
    <section className={[ui.card, s.skills].join(' ')} aria-labelledby="profile-skills">
      <div className={s.skillsHead}>
        <div className={s.skillsTitle}>
          <span id="profile-skills" className={ui.label}>
            {t('screens.profile.skills')}
          </span>
          <span className={ui.chip}>{chip}</span>
        </div>
        {desktop && toTraining}
      </div>
      <div className={s.mix}>
        <div className={s.mixBar} aria-hidden="true">
          {total === 0 ? (
            <span className={s.mixEmpty} />
          ) : (
            SKILL_GROUPS.map((g) => (
              <span key={g} className={s.mixSeg} style={{ flex: gp[g], background: topOn && g === top ? 'var(--amber)' : 'rgba(242,237,228,.22)' }} />
            ))
          )}
        </div>
        <div className={s.mixLegend}>
          <div className={s.mixCols}>
            {SKILL_GROUPS.map((g) => (
              <span key={g} className={[s.mixCol, topOn && g === top ? s.amber : ''].join(' ')}>
                {t(`screens.groups.${g}`)}
                <b>{total ? Math.round((gp[g] / total) * 100) : 0}%</b>
              </span>
            ))}
          </div>
          <span className={s.why}>{why}</span>
        </div>
      </div>
      <div className={s.groups}>
        {SKILL_GROUPS.map((g) => (
          <div key={g} className={s.groupBlock}>
            <div className={s.groupHead}>
              <span>{t(`screens.groups.${g}`)}</span>
              <span>{t('screens.training.groupPts', { count: gp[g] })}</span>
            </div>
            {skillsInGroup(g).map((d) => (
              <div key={d.id} className={s.skillRow}>
                <span className={s.skillIcon} aria-hidden="true">
                  {t(`screens.skills.${d.id}.abbr`)}
                </span>
                <span className={s.skillName}>{t(`screens.skills.${d.id}.name`)}</span>
                <span className={s.pips} aria-hidden="true">
                  {Array.from({ length: MAX_RANK }, (_, i) => (
                    <span key={i} className={[s.pip, i < ranks[d.id] ? s.pipOn : ''].join(' ')} />
                  ))}
                </span>
                <span className={s.skillRank}>
                  <span className={s.amber}>{ranks[d.id]}</span>
                  <span className={s.dim}>/{MAX_RANK}</span>
                </span>
              </div>
            ))}
          </div>
        ))}
      </div>
      {desktop ? (
        <div className={s.bottomGrid}>
          <Signature fighterId={fighterId} />
          <Perks />
        </div>
      ) : (
        <>
          <Perks />
          {toTraining}
          <Signature fighterId={fighterId} />
        </>
      )}
    </section>
  );
}

/**
 * The fighter choice: a tap picks a fighter the player has. A paid one (D52) shows its price, and a
 * tap tries it on (here and in the hero) until it's bought with the button below, as in the Locker.
 */
function FighterChoice({ tried, onTry }: { tried: string | null; onTry: (id: string | null) => void }) {
  const { t } = useTranslation();
  const manifest = useManifest();
  const fighterId = useMyFighter();
  const progress = useApp((st) => st.progress);
  const myLook = useMyLook();
  const pickFighter = useApp((st) => st.pickFighter);
  const buyFighter = useApp((st) => st.buyFighter);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  // The free fighters first, then the paid ones from the cheapest (the sort keeps the order).
  const fighters = useMemo(() => [...(manifest?.fighters ?? [])].sort((a, b) => fighterPrice(a.id) - fighterPrice(b.id)), [manifest]);
  const nameOf = (id: string) => t(`fighters.${id}`, { defaultValue: manifest?.fighters.find((f) => f.id === id)?.name ?? id });
  const price = tried ? fighterPrice(tried) : 0;
  const enough = progress.coins >= price;

  const pick = async (id: string) => {
    setFailed(false);
    if (!ownsFighter(progress, id)) return onTry(id);
    onTry(null);
    // The account's fighter (D55): back to the one it had when the server can't be reached.
    if (id !== fighterId && !(await pickFighter(id))) setFailed(true);
  };
  const buy = async () => {
    if (!tried || busy || !enough) return;
    setBusy(true);
    setFailed(false);
    const ok = await buyFighter(tried);
    setBusy(false);
    if (ok) onTry(null);
    else setFailed(true);
  };

  return (
    <section className={[ui.card, s.choice].join(' ')} aria-labelledby="profile-fighter">
      <div className={s.choiceHead}>
        <span id="profile-fighter" className={ui.label}>
          {t('screens.profile.fighter')}
        </span>
        <span className={ui.labelDim}>{t('screens.profile.fighterHint')}</span>
      </div>
      <div className={s.choiceGrid}>
        {fighters.map((f) => {
          const owned = ownsFighter(progress, f.id);
          const on = f.id === fighterId;
          const trying = f.id === tried;
          return (
            <button
              key={f.id}
              type="button"
              className={[s.choiceCard, on ? s.choiceOn : '', trying ? s.choiceTried : '', owned ? '' : s.choicePaid].join(' ')}
              aria-pressed={on || trying}
              onClick={() => void pick(f.id)}
            >
              <span className={s.choiceArt}>
                <FighterArt id={f.id} height={84} pose={on || trying ? 'bob' : 'still'} shadow="soft" alt="" look={myLook} />
              </span>
              <span className={s.choiceName}>{nameOf(f.id)}</span>
              {owned ? (
                <span className={s.choiceCheck} aria-hidden="true">
                  {on ? '✓' : ''}
                </span>
              ) : (
                <span className={s.choicePrice}>
                  <span className={s.coinDot} aria-hidden="true" />
                  {formatNumber(fighterPrice(f.id))}
                </span>
              )}
            </button>
          );
        })}
      </div>
      {tried && (
        <div className={s.buyRow}>
          <button
            type="button"
            className={[enough ? ui.primary : ui.secondary, s.buyBtn].join(' ')}
            onClick={() => void buy()}
            disabled={!enough || busy}
            aria-busy={busy}
          >
            <span className={s.buyText}>
              {enough
                ? t('screens.locker.buy', { item: nameOf(tried), price: formatNumber(price) })
                : t('screens.locker.needMore', { coins: formatNumber(price - progress.coins) })}
            </span>
          </button>
        </div>
      )}
      {failed && (
        <p className={s.saveFailed} role="alert">
          {t('common.saveFailed')}
        </p>
      )}
    </section>
  );
}

/** The Locker (M7): gloves, colours, effects and pets, bought with coins. */
function LockerLink() {
  const { t } = useTranslation();
  const go = useApp((st) => st.go);
  return (
    <button type="button" className={[ui.card, s.lockerLink].join(' ')} onClick={() => go('locker')}>
      <span className={s.lockerText}>
        <span className={s.lockerTitle}>{t('screens.locker.title')}</span>
        <span className={ui.labelDim}>{t('screens.profile.lockerHint')}</span>
      </span>
      <span className={ui.arrow} aria-hidden="true">
        →
      </span>
    </button>
  );
}

export function ProfileScreen() {
  const { t } = useTranslation();
  const layout = useLayoutMode();
  const desktop = layout === 'desktop';
  const progress = useApp((st) => st.progress);
  const fighterId = useMyFighter();
  const myLook = useMyLook();
  const startQuickFight = useApp((st) => st.startQuickFight);
  const now = useNow(60_000);
  const [editing, setEditing] = useState(false);
  // A paid fighter being tried on in the fighter choice (D52): the hero shows it too. Once it's
  // bought it's simply the player's fighter.
  const [trying, setTried] = useState<string | null>(null);
  const tried = trying && !ownsFighter(progress, trying) ? trying : null;
  useScrollTop();
  const p = currentProgress(progress, now);
  const since = monthYear(progress.createdAt);
  const editBtn = (
    <button type="button" className={[ui.secondary, s.editBtn].join(' ')} onClick={() => setEditing((e) => !e)} aria-expanded={editing}>
      {editing ? t('common.close') : t('screens.profile.edit')}
    </button>
  );
  const cta = (
    <button type="button" className={[ui.primary, s.cta].join(' ')} onClick={() => startQuickFight()}>
      {t('screens.home.quickFight')}
      <span className={ui.arrow} aria-hidden="true">
        ▷
      </span>
    </button>
  );
  const hero = (
    <div className={desktop ? s.deskHero : s.hero}>
      {desktop && <ArenaBackdrop tone="menu" shade="linear-gradient(180deg,rgba(20,19,18,.3),rgba(20,19,18,.95) 70%)" />}
      {desktop && <div className={s.deskEdit}>{editBtn}</div>}
      <div className={s.heroArt}>
        <FighterArt id={tried ?? fighterId} height={desktop ? 160 : 150} alt="" look={myLook} />
      </div>
      {/* Keyed: each edit starts from the current name (the account's may have arrived since). */}
      <NameEditor key={editing ? 'edit' : 'show'} editing={editing} onDone={() => setEditing(false)} className={s.name} />
      <div className={s.since}>{t('screens.profile.since', { level: levelView(p.xpTotal).level, date: since })}</div>
    </div>
  );

  if (desktop) {
    return (
      <div className={['cg-ui', ui.screen, s.desk].join(' ')}>
        <DesktopHeader />
        <main className={s.deskMain}>
          <aside className={s.deskAside}>
            {hero}
            <LevelCard progress={p} now={now} streak={false} />
            {cta}
          </aside>
          <section className={s.deskContent}>
            <Record />
            <Belts />
            <Skills desktop />
            <FighterChoice tried={tried} onTry={setTried} />
            <LockerLink />
          </section>
        </main>
      </div>
    );
  }

  return (
    <div className={['cg-ui', ui.screen].join(' ')}>
      <div className={s.phoneBg} aria-hidden="true">
        <ArenaBackdrop tone="menu" shade="linear-gradient(180deg,rgba(20,19,18,.8),rgba(20,19,18,.3) 45%,#141312)" />
      </div>
      <div className={ui.column}>
        <TopBar title={t('screens.profile.title')} right={editBtn} heading={false} />
        {hero}
        <div className={s.stack}>
          <LevelCard progress={p} now={now} streak={false} />
          <Record />
          <Belts />
          <Skills desktop={false} />
          <FighterChoice tried={tried} onTry={setTried} />
          <LockerLink />
          {cta}
        </div>
      </div>
    </div>
  );
}
